/**
 * MenuBar.tsx — the app's top chrome, implemented from the designer's "Menu Bar" frame
 * (Figma GaB9IJrgGQf6V8uI4WBYIu, node 7:300 → variant 7:301 "Full, Search=Bar", and the
 * profile dropdown 7:408 "Menu bar Dropdown").
 *
 * This REPLACES the old `.tb-header` + `.tb-nav` chrome (see App.tsx / frame.css). It is a
 * single horizontal row: roundel · nav · search · bell · profile. Built from the design
 * system's own primitives (Button, SearchInput geometry, menuIcons) on the design tokens,
 * not the old `--tb-*` set.
 *
 * ── Intentional deviations from the written product requirements, approved by the product
 *    owner as a redesign (not oversights) ──
 *
 *   • PC-F10 ("primary nav is EXACTLY these seven entries") — the frame trims the top-row nav
 *     to three category labels (Automations / Apps / AI & Skills). The other destinations move
 *     into the profile dropdown; upload (/share) and the tool catalogue (/tools) stay reachable
 *     from the Home page content. Accepted as an intentional IA redesign.
 *   • PC-F06a ("search is never reduced to an icon alone") — the frame carries a "Search=Icon"
 *     state, so the search bar collapses to a labelled icon button below the `stack` breakpoint
 *     and expands on press. The icon keeps an accessible label and is a real trigger, so it is
 *     not an unlabelled dead end.
 *
 * The three top-row category labels (Automations / Apps / AI & Skills) are the frame's own and
 * do NOT map to routes yet — the tool catalogue is not redesigned. They render as inert buttons
 * for now (no href), matching the frame; wiring them is a later pass.
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router';
import { VwRoundel } from '../../../brand';
import { SearchIcon } from './icons';

/** The frame's three top-row categories. Inert until the catalogue is redesigned (see header). */
const CATEGORIES = ['Automations', 'Apps', 'AI & Skills'] as const;

/**
 * The bar. `search` is the GlobalSearch element (App.tsx owns its state/behaviour); `bell` is the
 * NotificationBell; `profile` is the ProfileMenu (or a Sign in link when signed out). MenuBar owns
 * only the LAYOUT and the frame styling — the behaviour stays where it already lived.
 */
export function MenuBar({
  search,
  bell,
  profile,
}: {
  search: React.ReactNode;
  bell: React.ReactNode;
  profile: React.ReactNode;
}) {
  return (
    // 65px row, white, no wrap. The frame draws a fixed 1440 row; here it is fluid and the
    // middle nav group flexes so the roundel pins left and the account cluster pins right.
    //
    // The 1px bottom border runs the full width at the 65th pixel, separating the bar from
    // the page beneath it — the same ink @0.1 hairline as the internal dividers. Written as a
    // literal rgb() arbitrary value, NOT `border-b-ink/10`: `--ink` is a full hex, so the
    // opacity-modifier form compiles to invalid CSS and renders nothing (see divider comments).
    <header className="flex h-[65px] w-full items-center border-b border-[rgb(2_43_48_/_0.1)] bg-surface font-sans">
      {/* Roundel cell — 64px white square, 32px mark, centred (frame 7:302/7:303). */}
      <NavLink
        to="/"
        end
        aria-label="Trailblazer home"
        className="flex h-[65px] w-[64px] shrink-0 items-center justify-center text-ink outline-none focus-visible:border-ring-focus"
      >
        <span className="block size-[32px] [&_svg]:size-full">
          <VwRoundel />
        </span>
      </NavLink>

      {/* Divider — 1px hairline, ink @0.1 (frame 7:308). */}
      <span aria-hidden="true" className="block h-[65px] w-px shrink-0 bg-[rgb(2_43_48_/_0.1)]" />

      {/* Nav group — flexes to fill, gap-16, px-16 (frame 7:309). The category buttons are
          inert for now (no route); rendered as buttons to match the frame's Button style. */}
      <nav aria-label="Primary" className="flex h-[65px] flex-1 items-center gap-7 px-7">
        {CATEGORIES.map((label) => (
          <button
            key={label}
            type="button"
            className="rounded-md bg-surface px-5 py-2 text-sm font-light text-ink transition-colors hover:bg-accent-subtle"
          >
            {label}
          </button>
        ))}
      </nav>

      {/* Account cluster — search only now, right-aligned, px-16 (frame 7:313). The bell
          moved into the profile cell at the product owner's request. */}
      <div className="flex h-[65px] shrink-0 items-center justify-end px-7">{search}</div>

      {/* Divider before the profile cell (frame 7:316). */}
      <span aria-hidden="true" className="block h-[65px] w-px shrink-0 bg-[rgb(2_43_48_/_0.1)]" />

      {/* Profile cell (frame 7:317) — holds the bell to the LEFT of the profile. The cell's
          px-7 (16px) is the bell's left inset from the preceding divider; the gap-3 (8px)
          between the bell and the profile is the spacing between the two. */}
      <div className="flex h-[65px] shrink-0 items-center justify-center gap-3 px-7">
        {bell}
        {profile}
      </div>
    </header>
  );
}

/**
 * One row of the profile dropdown (frame 7:410–7:416): a 7px glyph + 11px Inter Light label,
 * px-12 py-3 rounded-8. Renders as whatever it needs to be — a router link, a plain button, or
 * an inert row — via the `as`/`to`/`onClick` props, so a single visual definition covers every
 * kind of destination the dropdown holds.
 */
export function DropdownRow({
  icon,
  label,
  to,
  onClick,
  role,
  ariaChecked,
}: {
  icon?: React.ReactNode;
  label: string;
  to?: string;
  onClick?: () => void;
  role?: string;
  ariaChecked?: boolean;
}) {
  const cls =
    'flex w-full items-center gap-4 rounded-md px-5 py-2 text-left text-xs font-light text-ink transition-colors hover:bg-accent-subtle focus-visible:bg-accent-subtle focus-visible:outline-none';
  const inner = (
    <>
      {icon ? (
        <span className="flex size-[14px] shrink-0 items-center justify-center text-icon">
          {icon}
        </span>
      ) : null}
      <span className="truncate">{label}</span>
    </>
  );

  if (to) {
    return (
      <NavLink to={to} role={role} className={cls} onClick={onClick}>
        {inner}
      </NavLink>
    );
  }
  return (
    <button
      type="button"
      role={role}
      {...(ariaChecked !== undefined ? { 'aria-checked': ariaChecked } : {})}
      className={cls}
      onClick={onClick}
    >
      {inner}
    </button>
  );
}

/**
 * A hairline divider between dropdown groups (frame 7:412/7:415): the design's teal tint.
 *
 * The frame draws this as a flat #E5F4F4, but that value IS `--accent-subtle` — the token is
 * `rgb(0 157 154 / 0.1)`, which composited over the white dropdown card gives rgb(230,245,245)
 * against the frame's rgb(229,244,244). One value per channel apart, and the same intent (the accent
 * at 10%), so this is the token written as a literal rather than a colour the ramp lacks. Using
 * `bg-accent-subtle` keeps it on the token — which is what audit-tokens.mjs checks for, and what
 * makes the divider follow the accent if the palette is ever reskinned.
 */
export function DropdownDivider() {
  return <span aria-hidden="true" className="my-2 block h-px w-full bg-accent-subtle" />;
}

/**
 * The dropdown card shell (frame 7:408): 243-wide white card, r8, soft teal shadow. `className`
 * appends to (and can override, being last) the base — the notification dropdown uses it to widen
 * the card, since its rows carry a title + timestamp rather than a single short label.
 */
export function DropdownCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role="menu"
      className={`absolute right-0 top-full z-20 mt-2 flex w-[243px] flex-col gap-2 rounded-md bg-surface p-4 shadow-[0px_2px_3px_2px_var(--accent-subtle)] ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * The responsive search surface. Wraps the app's GlobalSearch behaviour in the frame's look:
 * at `stack`+ it is the full accent-subtle bar (SearchInput geometry); below it collapses to a
 * 32×32 search-icon button that expands the bar on press (the frame's "Search=Icon" state).
 *
 * `expanded` only forces the bar open at narrow widths; at wide widths the bar is always shown.
 * Layout only — the actual input/label/dropdown are passed in as `children` from GlobalSearch.
 */
export function ResponsiveSearch({ children }: { children: React.ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Collapse again when focus leaves, so a narrow-width expand doesn't stick open forever.
  useEffect(() => {
    if (!expanded) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setExpanded(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [expanded]);

  return (
    <div ref={ref} className="relative flex items-center">
      {/* Collapsed trigger: shown only below `stack` AND only while not force-expanded. It
          keeps the bar's OWN look shrunk to a 32×32 square — accent-subtle fill, NO border —
          so it reads as the search bar in miniature, not a separate outlined control. The
          focus ring is the only border, and only on keyboard focus; a press expands the bar. */}
      {!expanded ? (
        <button
          type="button"
          className="flex size-[32px] items-center justify-center rounded-md border border-transparent bg-accent-subtle text-icon transition-colors hover:bg-[rgb(0_157_154_/_0.2)] focus-visible:border-ring-focus focus-visible:outline-none stack:hidden"
          aria-label="Search tools and people"
          onClick={() => setExpanded(true)}
        >
          <SearchIcon size={14} />
        </button>
      ) : null}
      {/* The bar: always shown at `stack`+; below it, shown only when expanded. */}
      <div className={expanded ? 'block' : 'hidden stack:block'}>{children}</div>
    </div>
  );
}
